import React, { useCallback } from 'react';
import styled from 'styled-components';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import PillTag from '../Pilltag';
import Typo from '../Typo';
import Textinput from '../Textinput';
import { useTranslation } from 'react-i18next';
import InfoIcon from '../InfoIconCard/InfoIcon';
import Flex from '../Flex';
import emailReg from '../../helpers/emailValidation';
import { theme } from '..';
import CheckboxButton from '../CheckboxButton';

const filter = createFilterOptions();

const AutocompleteStlyled = styled(Autocomplete)`
  &.MuiAutocomplete-root {
    color: ${theme.colors.NEU_PRIMARY} !important;
  }

  &.MuiAutocomplete-hasPopupIcon .MuiOutlinedInput-root {
    padding: 2px 40px 2px 3px;
    font-family: ${theme.fonts.TEXT_FONT}, sans-serif;
    color: ${theme.colors.NEU_PRIMARY};
  }
`;

const SearchSelect = ({
  multiple = false,
  dontCloseOnSelect = false,
  ownIcon,
  creatable,
  setValue,
  selectedOptions,
  id,
  options,
  label,
  optionLabel = 'name',
  translationLabel,
  optionType = 'default',
  onSearchChange,
  loading = false,
  getOptionDisabled,
  disableLocalFilter = false,
  ...props
}) => {
  const { t } = useTranslation();

  const translateItOrNot = (stringToTranslate) => {
    return !!translationLabel ? t(`${translationLabel}.${stringToTranslate}`) : stringToTranslate;
  };

  const handleChange = (newValue) => {
    setValue(newValue);
  };

  const onInputChange = useCallback(
    (e, value, reason) => {
      if (multiple && id === 'users') {
        const inputValue = e?.target?.value || value;
        if (inputValue && inputValue.includes(',')) {
          const result = inputValue.split(',')[0];
          setValue([...selectedOptions, { inputValue: result, name: result, email: result }]);
        }
      }

      if (onSearchChange && reason === 'input') {
        onSearchChange(value);
      }
    },
    [selectedOptions, id, multiple, setValue, onSearchChange],
  );

  return (
    <AutocompleteStlyled
      multiple={multiple}
      fullWidth
      loading={loading}
      slotProps={{ popper: { style: { zIndex: 10000 } } }}
      loadingText={t('globals.loading')}
      limitTags={2}
      openOnFocus={true}
      disableCloseOnSelect={multiple}
      onChange={(e, newValue) => handleChange(newValue)}
      onInputChange={onInputChange}
      isOptionEqualToValue={(option, value) => value && +option.id === +value.id}
      getOptionDisabled={getOptionDisabled || ((option) => (optionType === 'email' ? !emailReg.test(option.email) : false))}
      filterOptions={(options, params) => {
        if (disableLocalFilter) {
          if (creatable && params.inputValue !== '') {
            return [
              ...options,
              {
                inputValue: params.inputValue,
                name: `${t('globals.add')} "${params.inputValue}"`,
                email: params.inputValue,
              },
            ];
          }
          return options;
        }

        const filtered = filter(options, params);
        if (creatable) {
          if (params.inputValue !== '') {
            filtered.push({
              inputValue: params.inputValue,
              name: `${t('globals.add')} "${params.inputValue}"`,
              email: params.inputValue,
            });
          }
        }
        return filtered;
      }}
      value={selectedOptions}
      id={id}
      options={options}
      getOptionLabel={(option) => {
        // e.g value selected with enter, right from the input
        if (typeof option === 'string') return translateItOrNot(option);
        if (option && option.inputValue) return translateItOrNot(option.inputValue);
        if (option && option[optionLabel]) return translateItOrNot(option[optionLabel]);
        return '';
      }}
      selectOnFocus
      clearOnBlur
      renderTags={(value, getTagProps) =>
        value.map((option, index) => (
          <PillTag
            label={translateItOrNot(option[optionLabel] || (typeof option === 'string' ? option : ''))}
            {...getTagProps({ index })}
            key={`pill-${id}-${option.id || index}`}
          />
        ))
      }
      renderOption={(props, option, { selected }) => {
        return (
          <li {...props} key={option.id || option}>
            {multiple ? (
              <CheckboxButton value={optionLabel} labelText={translateItOrNot(option[optionLabel])} checked={selected} />
            ) : ownIcon ? (
              <Flex gap='10'>
                <InfoIcon size='small' iconName={option.iconName} />
                <Typo.Text>{translateItOrNot(option[optionLabel])}</Typo.Text>
              </Flex>
            ) : (
              <Typo.Text>{translateItOrNot(option[optionLabel] || (typeof option === 'string' ? option : ''))}</Typo.Text>
            )}
          </li>
        );
      }}
      noOptionsText={loading ? t('globals.loading') : t('globals.noOptions')}
      renderInput={(params) => <Textinput {...params} label={label} />}
      {...props}
    />
  );
};

export default SearchSelect;
